import { FC, useEffect, useRef } from "react";
import { TLatLng } from "../../types/map";

type TBasicMapPrintProps = {
  className?: string;
  printCenterPosition?: TLatLng;
  printMarkers?: TLatLng[];
  onMapReady?: () => void;
};

const BasicMapPrint: FC<TBasicMapPrintProps> = ({
  className = "",
  printCenterPosition,
  printMarkers = [],
  onMapReady,
}) => {

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const mapInitializedRef = useRef(false);
  const mapReadyRef = useRef(false);
  const markersRef = useRef<any[]>([]);
  
useEffect(() => {
  let cancelled = false;
  let retryTimer: number | undefined;
  let readyTimer: number | undefined;
  let fallbackTimer: number | undefined;

  const isReadyToInit = () => {
    const google = (window as any).google;
    const container = containerRef.current;
    return !!(
      google?.maps &&
      container &&
      container.offsetWidth > 0 &&
      container.offsetHeight > 0 &&
      printCenterPosition
    );
  };

  const markMapReady = () => {
    if (cancelled || mapReadyRef.current) return;

    requestAnimationFrame(() => {
      if (cancelled || mapReadyRef.current) return;

      readyTimer = window.setTimeout(() => {
        if (cancelled || mapReadyRef.current) return;
        mapReadyRef.current = true;
        onMapReady?.();
      }, 200);
    });
  };

  const waitForMapTiles = () => {
    if (cancelled || mapReadyRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const tileImages = container.querySelectorAll("img");
    if (tileImages.length > 0) {
      markMapReady();
      return;
    }
    retryTimer = window.setTimeout(waitForMapTiles, 100);
  };

  const createMarkers = (google: any, map: any) => {
    const markers = printMarkers.length ? printMarkers : [printCenterPosition!];

    if (markers.length === 1) {
      const position = { lat: markers[0].latitude, lng: markers[0].longitude };
      const marker = new google.maps.Marker({ position, map });
      markersRef.current.push(marker);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    markers.forEach((m) => {
      const position = { lat: m.latitude, lng: m.longitude };
      bounds.extend(position);
      const marker = new google.maps.Marker({ position, map });
      markersRef.current.push(marker);
    });
    map.fitBounds(bounds);
  };

  const initializeMap = () => {
    if (cancelled || mapReadyRef.current) return;
    if (mapInitializedRef.current && mapInstanceRef.current) return;

    if (!isReadyToInit()) {
      retryTimer = window.setTimeout(initializeMap, 100);
      return;
    }

    const google = (window as any).google;
    const mapContainer = containerRef.current!;
    const center = {
      lat: printCenterPosition!.latitude,
      lng: printCenterPosition!.longitude,
    };
    let map: any;
    try {
      map = new google.maps.Map(mapContainer, {
        center,
        zoom: 18,
        mapTypeId: "roadmap",
        disableDefaultUI: true,
        clickableIcons: false,
        gestureHandling: "none",
        keyboardShortcuts: false,
        draggable: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
      });
    } catch (error) {
      console.error("Error creating print map", error);
      mapInitializedRef.current = false;
      return;
    }

    mapInstanceRef.current = map;
    mapInitializedRef.current = true;

    createMarkers(google, map);

    requestAnimationFrame(() => {
      if (cancelled) return;
      google.maps.event.trigger(map, "resize");
      map.setCenter(center);
      waitForMapTiles();
    });

    fallbackTimer = window.setTimeout(() => {
      if (cancelled || mapReadyRef.current) return;
      markMapReady();
    }, 5000);
  };

  initializeMap();

  return () => {
    cancelled = true;

    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    if (readyTimer !== undefined) window.clearTimeout(readyTimer);
    if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
  };
}, []);

  return (
    <div className={`relative ${className}`}>
      <div
        ref={containerRef}
        className="w-full h-full"
      />
    </div>
  );
};
export default BasicMapPrint;